/**
 * Normalized lookup for data-contract DQX rules (quarantine `errors[].name` ↔ contract API).
 */

export interface DqxContractRuleMeta {
  rule_type: string;
  check_function?: string;
  affected_columns?: string[];
  allowed_values?: string[];
  total_column?: string;
  components?: string[];
}

export function normalizeDqxRuleName(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, " ");
}

function checkArguments(r: Record<string, unknown>): Record<string, unknown> | undefined {
  const check = r.check;
  if (!check || typeof check !== "object" || Array.isArray(check)) return undefined;
  const args = (check as { arguments?: unknown }).arguments;
  if (!args || typeof args !== "object" || Array.isArray(args)) return undefined;
  return args as Record<string, unknown>;
}

function affectedColumnsFromRuleRow(r: Record<string, unknown>): string[] | undefined {
  const top = r.affected_columns;
  if (Array.isArray(top)) {
    const out = top.map((x) => String(x).trim()).filter(Boolean);
    return out.length ? out : undefined;
  }
  const a = checkArguments(r);
  if (!a) return undefined;
  if (Array.isArray(a.columns) && a.columns.length > 0) {
    return a.columns.map((x) => String(x).trim()).filter(Boolean);
  }
  if (a.column != null && String(a.column).trim()) {
    return [String(a.column).trim()];
  }
  return undefined;
}

function stringArg(args: Record<string, unknown>, key: string): string | undefined {
  return typeof args[key] === "string" && (args[key] as string).trim()
    ? (args[key] as string).trim()
    : undefined;
}

function summationParamsFromRuleRow(r: Record<string, unknown>): {
  total_column?: string;
  components?: string[];
} {
  const args = checkArguments(r);
  if (!args) return {};
  const total_column = stringArg(args, "total_column");
  const components = Array.isArray(args.components)
    ? args.components.map((x) => String(x).trim()).filter(Boolean)
    : undefined;
  if (total_column && components?.length) {
    return { total_column, components };
  }
  // subtract_equals is "z_column = x_column + y_column" (no total_column/components
  // arguments) -- z_column is the total the other two sum into.
  const zColumn = stringArg(args, "z_column");
  const xColumn = stringArg(args, "x_column");
  const yColumn = stringArg(args, "y_column");
  if (zColumn && xColumn && yColumn) {
    return { total_column: zColumn, components: [xColumn, yColumn] };
  }
  return {
    ...(total_column ? { total_column } : {}),
    ...(components?.length ? { components } : {}),
  };
}

function allowedValuesFromRuleRow(r: Record<string, unknown>): string[] | undefined {
  const args = checkArguments(r);
  if (!args) return undefined;
  const allowed = args.allowed;
  if (!Array.isArray(allowed)) return undefined;
  const uniqueValues = new Set<string>();
  const out: string[] = [];
  for (const raw of allowed) {
    const value = String(raw).trim();
    if (!value || uniqueValues.has(value)) continue;
    uniqueValues.add(value);
    out.push(value);
  }
  return out.length ? out : undefined;
}

/**
 * Tier 1/2 metadata (crucially `rule_type`, which can be overridden per-field via
 * `rangeRuleType`) is keyed by DQX-style rule names (e.g. `{field}_invalid_range`). The
 * DuckDB engine's rule_ids use a different format (`{field}::{template_id}`), so without
 * this alias a per-field override would be invisible on DuckDB.
 */
const CHECK_FUNCTION_TO_DUCKDB_TEMPLATE: Record<string, string> = {
  is_not_null: "mandatory",
  is_valid_date: "valid_date",
  regex_match: "regex_match",
  is_in_range: "value_range",
};

function duckDbAliasKeyForRow(r: Record<string, unknown>, fieldName: string): string | undefined {
  const checkFunction = typeof r.check_function === "string" ? r.check_function.trim() : "";
  if (checkFunction === "length") {
    const args = checkArguments(r);
    const hasMin = args?.minLength != null;
    const hasMax = args?.maxLength != null;
    if (hasMin && hasMax) return `${fieldName}::length_range`;
    if (hasMax) return `${fieldName}::max_length`;
    if (hasMin) return `${fieldName}::min_length`;
    return undefined;
  }
  const template = CHECK_FUNCTION_TO_DUCKDB_TEMPLATE[checkFunction];
  return template ? `${fieldName}::${template}` : undefined;
}

/**
 * One map keyed by `normalizeDqxRuleName(rule.name)` for summary buckets, cell filters, and violation scope.
 */
export function buildContractRuleLookup(
  contractRules: Record<string, unknown>[]
): Map<string, DqxContractRuleMeta> {
  const m = new Map<string, DqxContractRuleMeta>();
  for (const r of contractRules) {
    const name = typeof r.name === "string" ? r.name.trim() : "";
    if (!name) continue;
    const key = normalizeDqxRuleName(name);
    const rule_type = typeof r.rule_type === "string" ? r.rule_type.trim() : "";
    const check_function =
      typeof r.check_function === "string" ? r.check_function.trim() : undefined;
    const affected_columns = affectedColumnsFromRuleRow(r);
    const allowed_values = allowedValuesFromRuleRow(r);
    const { total_column, components } = summationParamsFromRuleRow(r);
    const meta: DqxContractRuleMeta = {
      rule_type,
      ...(check_function ? { check_function } : {}),
      ...(affected_columns?.length ? { affected_columns } : {}),
      ...(allowed_values?.length ? { allowed_values } : {}),
      ...(total_column ? { total_column } : {}),
      ...(components?.length ? { components } : {}),
    };
    m.set(key, meta);

    const fieldName = typeof r.field === "string" ? r.field.trim() : affected_columns?.[0];
    const aliasKey = fieldName ? duckDbAliasKeyForRow(r, fieldName) : undefined;
    if (aliasKey) m.set(normalizeDqxRuleName(aliasKey), meta);
  }
  return m;
}

/**
 * Column-level lookup for constrained dropdown editing
 * Keyed by target column name, value is the sorted unique allowedValues list from the contract.
 */
export function buildColumnAllowedValuesLookup(
  contractRules: Record<string, unknown>[]
): Map<string, string[]> {
  const m = new Map<string, string[]>();
  for (const r of contractRules) {
    const affected_columns = affectedColumnsFromRuleRow(r);
    const allowed_values = allowedValuesFromRuleRow(r);

    if (!affected_columns?.length || !allowed_values?.length) continue;

    const sorted = [...allowed_values].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" })
    );

    for (const col of affected_columns) {
      m.set(col, sorted);
    }
  }
  return m;
}

export function lookupContractRuleMeta(
  lookup: Map<string, DqxContractRuleMeta> | null | undefined,
  ruleName: string
): DqxContractRuleMeta | undefined {
  if (!lookup || !ruleName.trim()) return undefined;
  return lookup.get(normalizeDqxRuleName(ruleName));
}
